import { CSSProperties } from "react";
import { NavLink } from "react-router-dom";
import { CurrentUser, logout } from "@/api/auth";

type MenuItem = {
  label: string;
  path: string;
  icon: string;
  badge?: number;
  external?: boolean;
};

export const MENU: MenuItem[] = [
  { label: "Dashboard", path: "/dashboard", icon: "dashboard" },
  { label: "Buchungen", path: "/bookings", icon: "event_note" },
  { label: "Kalender", path: "/calendar", icon: "calendar_month" },
  { label: "Anfragen", path: "/inquiries", icon: "mark_email_unread" },
  { label: "Statistik", path: "/statistik", icon: "insights" },
  { label: "Einstellungen", path: "/settings", icon: "settings" },
];

export const POSTEINGANG_ITEM: MenuItem = {
  label: "Posteingang",
  path: "/posteingang",
  icon: "inbox",
};

/** Liefert die sichtbaren Menüpunkte; „Posteingang“ wird nur bei aktivem Feature ergänzt. */
export function buildMenu(posteingangAktiv: boolean): MenuItem[] {
  if (!posteingangAktiv) return MENU;
  // Posteingang direkt nach „Anfragen“ einfügen.
  const idx = MENU.findIndex((m) => m.path === "/inquiries");
  const at = idx >= 0 ? idx + 1 : MENU.length;
  return [...MENU.slice(0, at), POSTEINGANG_ITEM, ...MENU.slice(at)];
}

const iconBtn = (): CSSProperties => ({
  width: 32,
  height: 32,
  border: "1px solid transparent",
  background: "transparent",
  borderRadius: 8,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "var(--fg-muted)",
  cursor: "pointer",
  transition: "background .12s ease, color .12s ease",
});

type Props = {
  collapsed: boolean;
  onToggle: () => void;
  user: CurrentUser | null;
  anfragenBadge: number;
  posteingangAktiv: boolean;
};

export function Sidebar({ collapsed, onToggle, user, anfragenBadge, posteingangAktiv }: Props) {
  const menu = buildMenu(posteingangAktiv);
  const w = collapsed ? "var(--sidebar-w-collapsed)" : "var(--sidebar-w)";
  const displayName = (user?.full_name || user?.username || "…").trim() || "…";
  const initials = displayName
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("");

  return (
    <aside
      style={{
        width: w,
        flexShrink: 0,
        background: "var(--bg-sidebar)",
        borderRight: "1px solid var(--border)",
        display: "flex",
        flexDirection: "column",
        position: "sticky",
        top: 0,
        height: "100vh",
        transition: "width .18s ease",
      }}
    >
      <div
        style={{
          height: "var(--topbar-h)",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: collapsed ? "0" : "0 16px",
          justifyContent: collapsed ? "center" : "flex-start",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            flexShrink: 0,
            background: "var(--brand-100)",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <img src="/app/LogoPZRM.png" alt="" style={{ width: 28, height: 28, objectFit: "contain" }} />
        </div>
        {!collapsed && (
          <div style={{ minWidth: 0, lineHeight: 1.15 }}>
            <div className="display" style={{ fontSize: 15, color: "var(--brand-900)", fontWeight: 400 }}>
              Raumünzach
            </div>
            <div style={{ fontSize: 11, color: "var(--fg-muted)" }}>Buchungssystem</div>
          </div>
        )}
      </div>

      <nav
        style={{
          flex: 1,
          padding: "12px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflowY: "auto",
        }}
      >
        {!collapsed && (
          <div
            style={{
              fontSize: 10,
              textTransform: "uppercase",
              letterSpacing: ".09em",
              color: "var(--fg-subtle)",
              padding: "10px 10px 6px",
              fontWeight: 600,
            }}
          >
            Arbeitsbereich
          </div>
        )}
        {menu.map((m) => {
          const computedBadge = m.path === "/inquiries" ? anfragenBadge : m.badge;
          const content = (active: boolean) => (
            <>
              {active && !collapsed && (
                <span
                  style={{
                    position: "absolute",
                    left: -8,
                    top: 6,
                    bottom: 6,
                    width: 3,
                    background: "var(--brand-700)",
                    borderRadius: 4,
                  }}
                />
              )}
              <span className="material-icons-outlined" style={{ fontSize: 20, flexShrink: 0 }}>
                {m.icon}
              </span>
              {!collapsed && <span style={{ flex: 1, textAlign: "left" }}>{m.label}</span>}
              {!collapsed && computedBadge ? (
                <span
                  style={{
                    background: "var(--accent)",
                    color: "#fff",
                    fontSize: 10,
                    fontWeight: 700,
                    padding: "1px 6px",
                    borderRadius: 999,
                    minWidth: 18,
                    textAlign: "center",
                  }}
                >
                  {computedBadge}
                </span>
              ) : null}
            </>
          );

          const linkStyle = (active: boolean): CSSProperties => ({
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: collapsed ? "10px 0" : "8px 10px",
            justifyContent: collapsed ? "center" : "flex-start",
            borderRadius: 8,
            border: "none",
            cursor: "pointer",
            background: active ? "var(--bg-selected)" : "transparent",
            color: active ? "var(--brand-900)" : "var(--fg-muted)",
            fontSize: 13,
            fontWeight: active ? 600 : 500,
            fontFamily: "var(--font-body)",
            position: "relative",
            textAlign: "left",
            textDecoration: "none",
            transition: "background .12s ease, color .12s ease",
          });

          if (m.external) {
            return (
              <a key={m.path} href={m.path} title={collapsed ? m.label : undefined} style={linkStyle(false)}>
                {content(false)}
              </a>
            );
          }
          return (
            <NavLink
              key={m.path}
              to={m.path}
              title={collapsed ? m.label : undefined}
              style={({ isActive }) => linkStyle(isActive)}
            >
              {({ isActive }) => content(isActive)}
            </NavLink>
          );
        })}
      </nav>

      <div style={{ borderTop: "1px solid var(--border)", padding: collapsed ? 8 : 10 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: collapsed ? "4px 0" : "6px 8px",
            borderRadius: 8,
            justifyContent: collapsed ? "center" : "flex-start",
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: "linear-gradient(135deg, var(--brand-700), var(--accent))",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {initials || "?"}
          </div>
          {!collapsed && (
            <div style={{ flex: 1, minWidth: 0, lineHeight: 1.2 }}>
              <div
                style={{
                  fontSize: 13,
                  color: "var(--fg)",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {displayName}
              </div>
              <div style={{ fontSize: 11, color: "var(--fg-muted)" }}>
                {user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : ""}
              </div>
            </div>
          )}
          {!collapsed && (
            <button title="Abmelden" onClick={() => logout()} style={iconBtn()}>
              <span className="material-icons-outlined" style={{ fontSize: 18 }}>
                logout
              </span>
            </button>
          )}
        </div>
        <button
          onClick={onToggle}
          style={{
            ...iconBtn(),
            width: "100%",
            marginTop: 4,
            justifyContent: "center",
            gap: 6,
            fontSize: 12,
            color: "var(--fg-subtle)",
          }}
        >
          <span className="material-icons-outlined" style={{ fontSize: 16 }}>
            {collapsed ? "chevron_right" : "chevron_left"}
          </span>
          {!collapsed && "Einklappen"}
        </button>
      </div>
    </aside>
  );
}
